/** DE per-field verdict (`FieldVerdict`), read defensively from the JSON blob. */
export type FieldVerdict = {
  groundTruth: unknown;
  extracted: unknown;
  skipped: boolean;
  strict: boolean | null;
  entityCredit: boolean | null;
  excludedM1: boolean;
  excludedM2: boolean;
};

/** A document's entity-credited accuracy tally over its non-skipped fields. */
export type DocAccuracy = {
  correct: number;
  total: number;
  pct: number | null;
};

/**
 * Entity-credited micro accuracy for one document, computed from its per-field
 * verdicts: over the non-skipped fields, the share credited a match. Mirrors the
 * aggregate "Entity" headline (`aggregate-tables`) at document granularity, so a
 * row and the run summary agree.
 *
 * @param verdicts The document's raw `verdicts` JSON (a `Record<field,
 *   FieldVerdict>`), or `null`/`undefined` when none was recorded.
 * @returns The `{ correct, total, pct }` tally; `pct` is `null` when the document
 *   has no scored (non-skipped) fields — still processing, errored before
 *   scoring, or ground truth missing.
 */
export const docEntityAccuracy = (verdicts: unknown): DocAccuracy => {
  if (verdicts === null || verdicts === undefined) {
    return { correct: 0, total: 0, pct: null };
  }
  let correct = 0;
  let total = 0;
  for (const v of Object.values(verdicts as Record<string, FieldVerdict>)) {
    if (!v.skipped) {
      total += 1;
      if (v.entityCredit === true) correct += 1;
    }
  }
  return { correct, total, pct: total === 0 ? null : (correct / total) * 100 };
};
